import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { PlantsService } from 'src/app/features/company/data/plants.service';
import { RecipesService } from "../data/recipes.service";
import { ConcreteEnvironmentsService } from "../../financial/data/concrete-environments.service";
import { ConcreteStrengthsService } from "../../financial/data/concrete-strengths.service";
import { ConcreteConsistenciesService } from "../../financial/data/concrete-consistencies.service";
import { CRUDAction } from "../../../components/JBM/Views/JBMCRUDTitle/JBMCRUDTitle.component";
import { TokenService } from "../../../services/auth/token.service";
import { RecipeResourcesComponent } from "./recipe-resources/recipe-resources.component";
import { HttpParams } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { JBMSelectOption } from '../../../components/JBM/Forms/JBMFormGroups/JBMSelect/JBMSelect.component';

@Component({
   selector: 'recipe-form',
   templateUrl: './recipe-form.component.html',
   styleUrls: ['./recipe-form.component.scss']
})
export class RecipeFormComponent implements OnInit {
   language: string = this.TokenService.getLanguage();
   recipe: any;
   action: CRUDAction;
   form: FormGroup;
   environments: JBMSelectOption[] = [];
   environment_ids: number[] = [];
   strengths: any[] = [];
   consistencies: any[] = [];
   strength_id: number = 0;
   consistency_id: number = 0;

   plants: any[] = []
   plantOptions: JBMSelectOption[]=[]       
   plantIds: number[]=[]

   linkedPlants: any = { managementApp: [], recipesApp: [] }
   submitted: boolean = false;

   constructor(
      private PlantsService: PlantsService,
      private RecipesService: RecipesService,
      private ConcreteEnvironmentsService: ConcreteEnvironmentsService,
      private ConcreteStrengthsService: ConcreteStrengthsService,
      private ConcreteConsistenciesService: ConcreteConsistenciesService,
      private TokenService: TokenService,
      private formBuilder: FormBuilder
   ) {
      this.recipe = { id: 0, description: '', internal_description: '', strengths_id: 0, consistencies_id: 0 }
      this.addControls();
   }

   @Input() plant_id: number;
   @Input() id: number;
   @Input() activeTab: number = 0;

   @Output() onClose = new EventEmitter();
   @Output() onSave = new EventEmitter();

   @ViewChild(RecipeResourcesComponent) recipeResources: RecipeResourcesComponent;

   ngOnInit(): void {
      if (this.id === 0) {
         this.action = CRUDAction.Create;
         this.getConcreteSpecs();
         this.getPlants()
         return;
      }
      this.action = CRUDAction.Update;
      let params = new HttpParams();
      params = params.append('concrete-specifications', '1');
      this.RecipesService.getOne(this.plant_id, this.id, this.language, params).subscribe(
         (data: any) => {
            this.recipe = data.data;
            this.environment_ids = this.recipe.environments;

            this.RecipesService.getLinkedPlants(this.recipe.public_id).subscribe(
               (data: any) => this.linkedPlants = data.data,
               (error: any) => console.error(error),
               () => this.getPlants()
            )
         }, (error: any) => console.error(error),
         () => this.getConcreteSpecs())
   }
   getPlants() {
      this.PlantsService.getData().subscribe(
         (data: any) => {
            this.plants = data.data
            this.setControlValues()
            this.setPlantOptions()
         }, (error: any) => console.error(error)
      )
   }
   setPlantOptions() {
      for (let plant of this.plants) {
         let selected = this.plantIds.indexOf(plant.id) > -1
         this.plantOptions.push({
            key: plant.id.toString(),
            value: plant.name,
            selected: selected,
            disabled: !this.allowUnlink(plant.id)
         })
      }
   }
   allowUnlink(plantId: number): boolean {
      const links = [...this.linkedPlants.managementApp, ...this.linkedPlants.recipesApp];

      // Not allowed to unlink when not allowed in one the apps for this plant
      for (const link of links) 
         if (link.plantId === plantId && !link.allowUnlink) 
            return false;

      return true;
   }   
   getConcreteSpecs() {
      let params = new HttpParams().append('sort', 'descriptions.description'); // Sort +description
      params = params.append('no-baseprice-indicators', '1');
      let envObservable = this.ConcreteEnvironmentsService.getData(this.language, params);
      let conObservable = this.ConcreteConsistenciesService.getData(this.language, params);
      let strObservable = this.ConcreteStrengthsService.getData(this.language, params);

      forkJoin([envObservable, conObservable, strObservable]).subscribe((results: any) => {
         let envData = results[0].data;
         this.environments = envData.map((e: any) => ({ key: e.id.toString(), value: e.code }));
         this.consistencies = results[1].data;
         this.strengths = results[2].data;
         this.setControlValues();
      });
   }
   addControls() {
      this.form = this.formBuilder.group({
         description: new FormControl('', { updateOn: 'blur', validators: [Validators.required] }),
         internal_description: new FormControl('', { updateOn: 'blur' })
      });
   }
   setControlValues() {
      this.form.controls.description.setValue(this.recipe.description);
      this.form.controls.internal_description.setValue(this.recipe.internal_description);
      this.strength_id = this.recipe.strengths_id;
      this.consistency_id = this.recipe.consistencies_id;
      this.environment_ids = this.recipe.environments;

      // Set plants selection
      this.plantIds=[]
      if(this.id) 
         for( let linkedPlant of this.linkedPlants.managementApp)
            this.plantIds.push(linkedPlant.plantId)               
      else   
         for( let plant of this.plants)
            this.plantIds.push(plant.id)               
   }
   get formControls() {
      return this.form.controls;
   }
   selectStrength(event: any) {
      this.strength_id = event.id;
   }
   selectConsistency(event: any) {
      this.consistency_id = event.id;
   }
   selectEnvironment(ids: number[]) {
      this.environment_ids = ids;
   }
   changePlants(selection: number[]) {
      this.plantIds=selection
   }
   save() {
      this.submitted = true;

      if (this.form.invalid)
         return;

      let data = {
         id: this.id,
         description: this.form.controls.description.value,
         internal_description: this.form.controls.internal_description.value,
         strengths_id: this.strength_id,
         consistencies_id: this.consistency_id,
         environment_ids: this.environment_ids.join(','),
         plantIds: this.plantIds
      }

      if (this.recipe.id)
         this.RecipesService.update(data).subscribe(
            () => { },
            (error: any) => this.handleError(error),
            () => this.afterSave())
      else
         this.RecipesService.create(data).subscribe(
            (data: any) => this.id = data.id,  // Primary key is needed for the resources
            (error: any) => this.handleError(error),
            () => this.afterSave())
   }
   handleError(error: string) {
      if (error == 'code_not_unique' || error == 'description_not_unique')
         this.formControls.description.setErrors({ notUnique: true });
      else
         console.error(error);
   }
   afterSave() {
      this.recipeResources.save(this.id);
      this.onSave.emit();
   }
   close() {
      this.onClose.emit();
   }
}
